import React, { useState, useEffect } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Box, Typography, Avatar, IconButton, Chip, Grid,
    TextField, MenuItem, Switch, Button, Alert, CircularProgress,
} from "@mui/material";
import GavelIcon from "@mui/icons-material/Gavel";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import StorefrontIcon from "@mui/icons-material/Storefront";

import { LOYALTY_API_BASE, SECTION_COLORS } from "../../constants";

const EditRuleDialog = ({ open, rule, onClose, onSaved }) => {
    const [form, setForm] = useState({ priority: 0, conditions: "", actions: "", active: true });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [actionCatalog, setActionCatalog] = useState([]);
    const [conditionCatalog, setConditionCatalog] = useState([]);
    const [selectedCondition, setSelectedCondition] = useState("");
    const [selectedAction, setSelectedAction] = useState("");

    useEffect(() => {
        if (rule) {
            setForm({
                priority: rule.priority ?? 0,
                conditions: rule.conditions ? JSON.stringify(rule.conditions, null, 2) : "",
                actions: rule.actions ? JSON.stringify(rule.actions, null, 2) : "",
                active: rule.active ?? true,
            });
            setError(null);

            // Try to extract selected types for the UI if it's a simple rule
            let sc = "";
            let sa = "";
            try {
                if (rule.conditions && rule.conditions.all && rule.conditions.all.length > 0) {
                    sc = Object.keys(rule.conditions.all[0])[0];
                }
            } catch (e) { }
            try {
                if (rule.actions && rule.actions.length > 0 && rule.actions[0].type) {
                    sa = rule.actions[0].type;
                }
            } catch (e) { }

            setSelectedCondition(sc);
            setSelectedAction(sa);

            // Fetch Catalogs
            fetch(`${LOYALTY_API_BASE}/admin/rule-conditions`, { headers: { "X-Brand": rule.brand } })
                .then(res => res.ok ? res.json() : { conditions: [] })
                .then(data => setConditionCatalog(data.conditions || []))
                .catch(err => console.error("Failed to load conditions catalog", err));

            fetch(`${LOYALTY_API_BASE}/admin/rule-actions`, { headers: { "X-Brand": rule.brand } })
                .then(res => res.ok ? res.json() : { actions: [] })
                .then(data => setActionCatalog(data.actions || []))
                .catch(err => console.error("Failed to load actions catalog", err));
        }
    }, [rule]);

    const handle = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));

    const handleInsertCondition = (e) => {
        const type = e.target.value;
        setSelectedCondition(type);
        if (!type) return;
        const conditionConfig = conditionCatalog.find(c => c.type === type);
        let defaultVal = '""';
        if (conditionConfig && conditionConfig.params === "int") defaultVal = 0;
        if (conditionConfig && conditionConfig.params === true) defaultVal = true;
        const template = `{\n  "all": [\n    {\n      "${type}": ${defaultVal}\n    }\n  ]\n}`;
        setForm(p => ({ ...p, conditions: p.conditions ? p.conditions + "\n" + template : template }));
    };

    const handleInsertAction = (e) => {
        const type = e.target.value;
        setSelectedAction(type);
        if (!type) return;
        const actionConfig = actionCatalog.find(a => a.type === type);
        const example = actionConfig?.examples?.[0] || { type };
        const template = `[\n  ${JSON.stringify(example, null, 2).replace(/\n/g, '\n  ')}\n]`;
        setForm(p => ({ ...p, actions: p.actions ? p.actions + "\n" + template : template }));
    };

    const handleSubmit = async () => {
        if (!rule) return;
        setSubmitting(true);
        setError(null);
        try {
            let conditions = null;
            if (form.conditions.trim()) {
                try { conditions = JSON.parse(form.conditions); }
                catch { throw new Error("conditions n'est pas un JSON valide"); }
            }
            let actions = null;
            if (form.actions.trim()) {
                try {
                    actions = JSON.parse(form.actions);
                    if (!Array.isArray(actions)) throw new Error();
                } catch { throw new Error("actions doit être un tableau JSON valide"); }
            }

            const body = { priority: Number(form.priority), conditions, actions, active: form.active };
            const res = await fetch(`${LOYALTY_API_BASE}/rules/${rule.id}`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    "X-Brand": rule.brand
                },
                body: JSON.stringify(body),
            });
            if (!res.ok) { const e = await res.json().catch(() => null); throw new Error(e?.detail || `Erreur HTTP ${res.status}`); }
            onSaved(); onClose();
        } catch (err) { setError(err.message); } finally { setSubmitting(false); }
    };

    if (!rule) return null;

    const fieldSx = { "& .MuiOutlinedInput-root": { borderRadius: 2 } };
    const monoFieldSx = { ...fieldSx, "& textarea": { fontFamily: "monospace", fontSize: 12 } };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ borderBottom: "2px solid #f1f3f5", px: 3, py: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ width: 32, height: 32, background: SECTION_COLORS.rules.gradient }}>
                        <GavelIcon sx={{ fontSize: 18 }} />
                    </Avatar>
                    <Box>
                        <Typography variant="h6" fontFamily="Montserrat, sans-serif" fontWeight={700} color="#1a1a2e" fontSize={16}>
                            Modifier la Règle
                        </Typography>
                        <Typography fontSize={11} color="text.secondary">{rule.brand} • {rule.event_type}</Typography>
                    </Box>
                </Box>
                <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: "24px !important", px: 3, pb: 2 }}>
                {/* Read-only info */}
                <Box display="flex" gap={1} mb={2.5} flexWrap="wrap">
                    <Chip icon={<StorefrontIcon sx={{ fontSize: 14 }} />} label={rule.brand} size="small"
                        sx={{ fontWeight: 700, fontSize: 12, bgcolor: `${SECTION_COLORS.rules.main}12`, color: SECTION_COLORS.rules.main }} />
                    <Chip label={`Événement : ${rule.event_type}`} size="small"
                        sx={{ fontWeight: 700, fontSize: 12, bgcolor: SECTION_COLORS.rules.light, color: SECTION_COLORS.rules.main }} />
                    <Typography fontSize={11} color="text.secondary" alignSelf="center">
                        ⚠️ Marque et type d'événement non modifiables
                    </Typography>
                </Box>

                <Grid container spacing={2}>
                    {/* priority */}
                    <Grid item xs={12} sm={4}>
                        <TextField fullWidth label="Priorité" type="number" value={form.priority}
                            onChange={(e) => setForm((p) => ({ ...p, priority: Number(e.target.value) }))}
                            size="small" helperText="Plus élevé = exécuté en premier"
                            InputProps={{ inputProps: { min: 0 } }} sx={fieldSx} />
                    </Grid>
                    <Grid item xs={12} sm={8}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" px={1.5} py={1}
                            sx={{ borderRadius: 2, bgcolor: "#fafbfd", border: "1px solid #e8ecf1", height: "100%" }}>
                            <Box>
                                <Typography fontSize={13} fontWeight={600}>Activer</Typography>
                                <Typography fontSize={11} color="text.secondary">Règle appliquée</Typography>
                            </Box>
                            <Switch checked={form.active} onChange={() => setForm((p) => ({ ...p, active: !p.active }))}
                                sx={{
                                    "& .MuiSwitch-switchBase.Mui-checked": { color: SECTION_COLORS.rules.main },
                                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: SECTION_COLORS.rules.main }
                                }} />
                        </Box>
                    </Grid>

                    {/* conditions */}
                    <Grid item xs={12}>
                        <TextField fullWidth select size="small" label="Condition" value={selectedCondition}
                            onChange={handleInsertCondition} sx={{ ...fieldSx }}
                            helperText="Sélectionnez la condition applicable. Laissez vide pour exécuter inconditionnellement.">
                            <MenuItem value=""><em>Aucune condition</em></MenuItem>
                            {conditionCatalog.map(c => (
                                <MenuItem key={c.type} value={c.type}>{c.type}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    {/* actions */}
                    <Grid item xs={12}>
                        <TextField fullWidth select size="small" label="Action" value={selectedAction}
                            onChange={handleInsertAction} sx={{ ...fieldSx }}
                            helperText="Sélectionnez l'action à exécuter." required>
                            {actionCatalog.map(a => (
                                <MenuItem key={a.type} value={a.type}>{a.title || a.type}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                </Grid>

                {error && <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>{error}</Alert>}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e0e0e0", gap: 1 }}>
                <Button onClick={onClose} sx={{ color: "text.secondary", textTransform: "none" }}>Annuler</Button>
                <Button variant="contained" onClick={handleSubmit} disabled={submitting}
                    startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                    sx={{ background: SECTION_COLORS.rules.gradient, textTransform: "none", borderRadius: 2, fontWeight: 600, px: 3 }}>
                    {submitting ? "Enregistrement..." : "Enregistrer"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditRuleDialog;
